import React, { useEffect, useState, lazy, Suspense } from 'react';
import {
  Routes,
  Route,
  useLocation
} from 'react-router-dom';
import 'aos/dist/aos.css';
import './css/style.css';
import AOS from 'aos';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import Home from './pages/Home';
import ConsentBanner from './components/ConsentBanner';
import ConsentSettingsModal from './components/ConsentSettingsModal';
import ProtectedRoute from './components/ProtectedRoute';
import Breadcrumbs from './components/Breadcrumbs';

const SignIn = lazy(() => import('./pages/SignIn'));
const SignUp = lazy(() => import('./pages/SignUp'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Settings = lazy(() => import('./pages/Settings'));
const Standards = lazy(() => import('./pages/Standards'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Terms = lazy(() => import('./pages/Terms'));
const Imprint = lazy(() => import('./pages/Imprint'));
const SecurityHallOfFame = lazy(() => import('./pages/SecurityHallOfFame'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/products/ProductDetail'));
const Job = lazy(() => import('./pages/Job'));
const Blogs = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/Blogs/intro'));
const MeridianPost = lazy(() => import('./pages/Blogs/meridian2024'));
const StandardsPost = lazy(() => import('./pages/Blogs/standards'));
const Ebsi = lazy(() => import('./pages/Blogs/ebsi'));
const DigitalPaymentsFuture = lazy(() => import('./pages/Blogs/digitalPaymentsFuture'));
const EarthquakeStandards = lazy(() => import('./pages/Blogs/earthquakeStandards'));
const EntrepreneurshipAvenue2026 = lazy(() => import('./pages/Blogs/entrepreneurshipAvenue2026'));
const OgCrmTicketing = lazy(() => import('./pages/Blogs/ogCrmTicketing'));
const HtmlToImage = lazy(() => import('./pages/HtmlToImage'));
const ScreenshotToImage = lazy(() => import('./pages/ScreenshotToImage'));
const PdfTools = lazy(() => import('./pages/pdfTools'));
const SecurityTools = lazy(() => import('./pages/securityTools'));
const BlockchainCompliance = lazy(() => import('./pages/blockchainCompliance'));
const Iso27001GapAnalysis = lazy(() => import('./pages/iso27001'));
const Iso9001ReadinessChecker = lazy(() => import('./pages/iso9001'));
const Soc2ReadinessChecker = lazy(() => import('./pages/soc2'));
const Iso42001AiReadiness = lazy(() => import('./pages/iso42001'));
const Iso8601Validator = lazy(() => import('./pages/iso8601'));
const EthereumToolkit = lazy(() => import('./pages/ethereumToolkit'));
const AwsArnParser = lazy(() => import('./pages/awsArnParser'));
const IamPolicyValidator = lazy(() => import('./pages/iamPolicyValidator'));
const SapODataUrlBuilder = lazy(() => import('./pages/sapODataUrlBuilder'));
const ServiceNowQueryBuilder = lazy(() => import('./pages/servicenowQueryBuilder'));
const HashGenerator = lazy(() => import('./pages/hashGenerator'));
const IbanValidator = lazy(() => import('./pages/ibanValidator'));
const BicValidator = lazy(() => import('./pages/bicValidator'));
const LeiValidator = lazy(() => import('./pages/leiValidator'));
const Iso20022Viewer = lazy(() => import('./pages/iso20022'));
const Mt940ToCsv = lazy(() => import('./pages/mt940ToCsv'));
const Mt940ToCamt053 = lazy(() => import('./pages/mt940ToCamt053'));
const Hl7Parser = lazy(() => import('./pages/hl7Parser'));
const FhirValidator = lazy(() => import('./pages/fhirValidator'));
const TleParser = lazy(() => import('./pages/tleParser'));
const TleConverter = lazy(() => import('./pages/tleConverter'));
const ScormValidator = lazy(() => import('./pages/scormValidator'));
const XapiStatementBuilder = lazy(() => import('./pages/xapiStatementBuilder'));
const GtinValidator = lazy(() => import('./pages/gtinValidator'));
const Gs1BarcodeParser = lazy(() => import('./pages/gs1BarcodeParser'));
const GeoChecker = lazy(() => import('./pages/geoChecker'));
const PhoneFormatter = lazy(() => import('./pages/phoneFormatter'));
const VcardGenerator = lazy(() => import('./pages/vcardGenerator'));
const EmailValidator = lazy(() => import('./pages/emailValidator'));
const CsvDeduplicator = lazy(() => import('./pages/csvDeduplicator'));
const WordCounter = lazy(() => import('./pages/wordCounter'));
const IpfsCidInspector = lazy(() => import('./pages/ipfsCidInspector'));
const IpfsGatewayChecker = lazy(() => import('./pages/ipfsGatewayChecker'));
const IpfsCidGenerator = lazy(() => import('./pages/ipfsCidGenerator'));
const IpfsUrlConverter = lazy(() => import('./pages/ipfsUrlConverter'));
const Tools = lazy(() => import('./pages/Tools'));
const XmlTools = lazy(() => import('./pages/xmlTools'));
const XmlFormatterPage = lazy(() => import('./pages/xmlTools/XmlFormatterPage'));
const XmlValidatorPage = lazy(() => import('./pages/xmlTools/XmlValidatorPage'));
const XmlToJsonPage = lazy(() => import('./pages/xmlTools/XmlToJsonPage'));
const XmlMinifierPage = lazy(() => import('./pages/xmlTools/XmlMinifierPage'));
const XPathTesterPage = lazy(() => import('./pages/xmlTools/XPathTesterPage'));
const XmlToCsvPage = lazy(() => import('./pages/xmlTools/XmlToCsvPage'));
const XsdValidatorPage = lazy(() => import('./pages/xmlTools/XsdValidatorPage'));
const SepaXmlValidator = lazy(() => import('./pages/sepaValidator'));
const SepaXmlGenerator = lazy(() => import('./pages/sepaXmlGenerator'));
const PdfMergePage = lazy(() => import('./pages/pdfTools/PdfMergePage'));
const PdfSplitPage = lazy(() => import('./pages/pdfTools/PdfSplitPage'));
const ImageToPdfPage = lazy(() => import('./pages/pdfTools/ImageToPdfPage'));
const DocToPdfPage = lazy(() => import('./pages/pdfTools/DocToPdfPage'));
const PdfToWordPage = lazy(() => import('./pages/pdfTools/PdfToWordPage'));
const PdfMetadataPage = lazy(() => import('./pages/pdfTools/PdfMetadataPage'));
const WeiConverterPage = lazy(() => import('./pages/ethereumToolkit/WeiConverterPage'));
const Keccak256Page = lazy(() => import('./pages/ethereumToolkit/Keccak256Page'));
const FunctionSelectorPage = lazy(() => import('./pages/ethereumToolkit/FunctionSelectorPage'));
const EventTopicPage = lazy(() => import('./pages/ethereumToolkit/EventTopicPage'));
const AbiEncoderPage = lazy(() => import('./pages/ethereumToolkit/AbiEncoderPage'));
const AddressChecksumPage = lazy(() => import('./pages/ethereumToolkit/AddressChecksumPage'));
const DataProtection = lazy(() => import('./pages/DataProtection'));
const DataPolicy = lazy(() => import('./pages/DataPolicy'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Quote = lazy(() => import('./pages/Quote'));
const Dora = lazy(() => import('./pages/Dora'));
const HelpDesk = lazy(() => import('./pages/HelpDesk'));
const HelpDeskTicketDetail = lazy(() => import('./pages/HelpDeskTicketDetail'));
const CRM = lazy(() => import('./pages/CRM'));
const ExpertNetwork = lazy(() => import('./pages/ExpertNetwork'));
const ExpertApplications = lazy(() => import('./pages/ExpertApplications'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Leads = lazy(() => import('./pages/Leads'));
const Opportunities = lazy(() => import('./pages/Opportunities'));
const Activities = lazy(() => import('./pages/Activities'));
const Import = lazy(() => import('./pages/Import'));
const Companies = lazy(() => import('./pages/Companies'));
const Ventures = lazy(() => import('./pages/Ventures'));
const About = lazy(() => import('./pages/About'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const PortfolioProject = lazy(() => import('./pages/portfolio/ProjectDetail'));
const Partners = lazy(() => import('./pages/Partners'));
const ExpertHub = lazy(() => import('./pages/ExpertHub'));
const Publications = lazy(() => import('./pages/Publications'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/PaymentCancel'));
const Insights = lazy(() => import('./pages/Insights'));
const Iso20022Migration = lazy(() => import('./pages/Insights/iso20022Migration'));
const Iso42001Guide = lazy(() => import('./pages/Insights/iso42001Guide'));
const IamPolicySecurity = lazy(() => import('./pages/Insights/iamPolicySecurity'));
const Hl7VsFhir = lazy(() => import('./pages/Insights/hl7VsFhir'));
const EthereumCalldata = lazy(() => import('./pages/Insights/ethereumCalldata'));
const Iso27001GapAnalysisGuide = lazy(() => import('./pages/Insights/iso27001GapAnalysisGuide'));
const Iso27001GapTemplate = lazy(() => import('./pages/Insights/iso27001GapTemplate'));
const EthereumWeiGweiUnits = lazy(() => import('./pages/Insights/ethereumWeiGweiUnits'));
const Hl7V2MessageStructure = lazy(() => import('./pages/Insights/hl7V2MessageStructure'));
const AwsIamPolicyValidation = lazy(() => import('./pages/Insights/awsIamPolicyValidation'));
const DoraCryptoWeb3 = lazy(() => import('./pages/Insights/doraCryptoWeb3'));
const StellarCrossBorderPayments = lazy(() => import('./pages/Insights/stellarCrossBorderPayments'));
const Iso27001Soa = lazy(() => import('./pages/Insights/iso27001Soa'));
const Iso27001AnnexA = lazy(() => import('./pages/Insights/iso27001AnnexA'));
const Soc2TrustServicesCriteria = lazy(() => import('./pages/Insights/soc2TrustServicesCriteria'));
const Soc2VsIso27001 = lazy(() => import('./pages/Insights/soc2VsIso27001'));
const Iso42001AnnexA = lazy(() => import('./pages/Insights/iso42001AnnexA'));
const IntegratedManagementSystem = lazy(() => import('./pages/Insights/integratedManagementSystem'));
const Iso8601Format = lazy(() => import('./pages/Insights/iso8601Format'));
const HowToMergePdfFiles = lazy(() => import('./pages/Insights/howToMergePdfFiles'));
const PdfMetadataGuide = lazy(() => import('./pages/Insights/pdfMetadataGuide'));
const Mt940Format = lazy(() => import('./pages/Insights/mt940Format'));
const Eidas2EudiWallet = lazy(() => import('./pages/Insights/eidas2EudiWallet'));
const OdataQueryOptions = lazy(() => import('./pages/Insights/odataQueryOptions'));
const ChooseCrmTicketing = lazy(() => import('./pages/Insights/chooseCrmTicketing'));
const CrmComparison = lazy(() => import('./pages/Insights/crmComparison'));
const HelpdeskWorkflow = lazy(() => import('./pages/Insights/helpdeskWorkflow'));
const Iso9001ImplementationGuide = lazy(() => import('./pages/Insights/iso9001ImplementationGuide'));
const Mt940FieldReference = lazy(() => import('./pages/Insights/mt940FieldReference'));
const OdataFilterExamples = lazy(() => import('./pages/Insights/odataFilterExamples'));
const O1jsStateAssertions = lazy(() => import('./pages/Insights/o1jsStateAssertions'));
const Iso24874StellarSdg = lazy(() => import('./pages/Insights/iso24874StellarSdg'));
const ClaudeCodeMcp = lazy(() => import('./pages/Insights/claudeCodeMcp'));
const Gpt6ModelTiers = lazy(() => import('./pages/Insights/gpt6ModelTiers'));
const DevinAutonomous = lazy(() => import('./pages/Insights/devinAutonomous'));
const DeepseekV4 = lazy(() => import('./pages/Insights/deepseekV4'));
const GeminiArgon = lazy(() => import('./pages/Insights/geminiArgon'));
const KimiK3 = lazy(() => import('./pages/Insights/kimiK3'));
const Glm5Zai = lazy(() => import('./pages/Insights/glm5Zai'));
const EuAiActCompliance = lazy(() => import('./pages/Insights/euAiActCompliance'));
const Nis2Compliance = lazy(() => import('./pages/Insights/nis2Compliance'));
const CyberResilienceAct = lazy(() => import('./pages/Insights/cyberResilienceAct'));
const En18286AiQms = lazy(() => import('./pages/Insights/en18286AiQms'));
const AssetTokenization = lazy(() => import('./pages/Insights/assetTokenization'));
const IpfsCidExplained = lazy(() => import('./pages/Insights/ipfsCidExplained'));
const DigitalOmnibus = lazy(() => import('./pages/Insights/digitalOmnibus'));
const VerificationOfPayee = lazy(() => import('./pages/Insights/verificationOfPayee'));
const AgenticPayments = lazy(() => import('./pages/Insights/agenticPayments'));
const BestFreeCrm = lazy(() => import('./pages/Insights/bestFreeCrm'));
const CrmDataMigration = lazy(() => import('./pages/Insights/crmDataMigration'));
const Glossary = lazy(() => import('./pages/glossary'));
const GlossaryTerm = lazy(() => import('./pages/glossary/GlossaryTerm'));


function App() {

  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  useEffect(() => {
    AOS.init({
      once: true,
      disable: 'phone',
      duration: 600,
      easing: 'ease-out-sine',
    });
  });

  useEffect(() => {
    document.querySelector('html').style.scrollBehavior = 'auto'
    window.scroll({ top: 0 })
    document.querySelector('html').style.scrollBehavior = ''
  }, [location.pathname]); // triggered on route change

  // Set up global function for cookie settings
  useEffect(() => {
    window.openCookieSettings = () => setShowCookieSettings(true);
    return () => {
      delete window.openCookieSettings;
    };
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <Breadcrumbs />
        <Suspense fallback={<div data-prerender-loading className="flex items-center justify-center min-h-screen bg-black"><div className="text-gray-400">Loading...</div></div>}>
        <Routes>
        <Route exact path="/terms" element={<Terms/>} />
        <Route exact path="/" element={<Home />} />
        <Route exact path="/signin" element={<SignIn/>} />
        <Route exact path="/signup" element={<SignUp/>} />
        <Route exact path="/auth/callback" element={<AuthCallback/>} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/standards" element={<Standards />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route exact path="/imprint" element={<Imprint/>} />
        <Route exact path="/security-hall-of-fame" element={<SecurityHallOfFame/>} />
        <Route exact path="/privacy" element={<DataProtection/>} />
        <Route exact path="/cookie-policy" element={<DataPolicy/>} />
        <Route exact path="/products" element={<Products/>} />
        <Route exact path="/products/:slug" element={<ProductDetail/>} />
        <Route exact path="/careers" element={<Job/>} />
        <Route exact path="/quote" element={<Quote/>} />
        <Route exact path="/dora" element={<Dora/>} />
        <Route exact path="/helpdesk" element={<HelpDesk/>} />
        <Route path="/helpdesk/ticket/:id" element={
          <ProtectedRoute>
            <HelpDeskTicketDetail />
          </ProtectedRoute>
        } />
        <Route path="/crm" element={<CRM/>} />
        <Route path="/crm/contacts" element={<Contacts/>} />
        <Route path="/crm/leads" element={<Leads/>} />
        <Route path="/crm/opportunities" element={<Opportunities/>} />
        <Route path="/crm/activities" element={<Activities/>} />
        <Route path="/crm/import" element={<Import/>} />
        <Route path="/crm/companies" element={<Companies/>} />
        <Route path="/crm/expert-applications" element={<ExpertApplications/>} />
        <Route path="/expert-network" element={<ExpertNetwork/>} />
        <Route exact path="/ventures" element={<Ventures/>} />
        <Route exact path="/about" element={<About/>} />
        <Route exact path="/portfolio" element={<Portfolio/>} />
        <Route exact path="/portfolio/:slug" element={<PortfolioProject/>} />
        <Route exact path="/partners" element={<Partners/>} />
        <Route exact path="/expert-hub" element={<ExpertHub/>} />
        <Route exact path="/publications" element={<Publications/>} />
        <Route exact path="/blog" element={<Blogs/>} />
        <Route exact path="/blog/reaching-new-frontiers" element={<BlogPost/>} />
        <Route exact path="/blog/meridian-2024-highlights" element={<MeridianPost/>} />
        <Route exact path="/blog/ebsi-verifiable-credentials" element={<Ebsi/>} />
        <Route exact path="/blog/how-blockchain-standards-enable-enterprises-to-reach-global-customers" element={<StandardsPost/>} />
        <Route exact path="/blog/digital-payments-future" element={<DigitalPaymentsFuture/>} />
        <Route exact path="/blog/estandares-sismicos-colombia" element={<EarthquakeStandards/>} />
        <Route exact path="/blog/entrepreneurship-avenue-2026" element={<EntrepreneurshipAvenue2026/>} />
        <Route exact path="/blog/introducing-og-crm-ticketing" element={<OgCrmTicketing/>} />
        <Route exact path="/insights" element={<Insights/>} />
        <Route exact path="/insights/iso-20022-migration-guide" element={<Iso20022Migration/>} />
        <Route exact path="/insights/iso-42001-ai-management-guide" element={<Iso42001Guide/>} />
        <Route exact path="/insights/iam-policy-security-patterns" element={<IamPolicySecurity/>} />
        <Route exact path="/insights/hl7-v2-vs-fhir-comparison" element={<Hl7VsFhir/>} />
        <Route exact path="/insights/ethereum-calldata-abi-guide" element={<EthereumCalldata/>} />
        <Route exact path="/insights/iso-27001-gap-analysis-guide" element={<Iso27001GapAnalysisGuide/>} />
        <Route exact path="/insights/iso-27001-gap-analysis-template" element={<Iso27001GapTemplate/>} />
        <Route exact path="/insights/ethereum-wei-gwei-units" element={<EthereumWeiGweiUnits/>} />
        <Route exact path="/insights/hl7-v2-message-structure" element={<Hl7V2MessageStructure/>} />
        <Route exact path="/insights/aws-iam-policy-validation" element={<AwsIamPolicyValidation/>} />
        <Route exact path="/insights/dora-crypto-web3-compliance" element={<DoraCryptoWeb3/>} />
        <Route exact path="/insights/stellar-cross-border-payments-anchors-sep24" element={<StellarCrossBorderPayments/>} />
        <Route exact path="/insights/iso-27001-statement-of-applicability" element={<Iso27001Soa/>} />
        <Route exact path="/insights/iso-27001-annex-a-controls" element={<Iso27001AnnexA/>} />
        <Route exact path="/insights/soc-2-trust-services-criteria" element={<Soc2TrustServicesCriteria/>} />
        <Route exact path="/insights/soc-2-vs-iso-27001" element={<Soc2VsIso27001/>} />
        <Route exact path="/insights/iso-42001-annex-a-controls" element={<Iso42001AnnexA/>} />
        <Route exact path="/insights/integrated-management-system-iso-9001-27001-42001" element={<IntegratedManagementSystem/>} />
        <Route exact path="/insights/iso-8601-format-guide" element={<Iso8601Format/>} />
        <Route exact path="/insights/how-to-merge-pdf-files" element={<HowToMergePdfFiles/>} />
        <Route exact path="/insights/pdf-metadata-guide" element={<PdfMetadataGuide/>} />
        <Route exact path="/insights/mt940-format-camt053-migration" element={<Mt940Format/>} />
        <Route exact path="/insights/eidas2-eudi-wallet" element={<Eidas2EudiWallet/>} />
        <Route exact path="/insights/odata-query-options-guide" element={<OdataQueryOptions/>} />
        <Route exact path="/insights/how-to-choose-crm-ticketing-system" element={<ChooseCrmTicketing/>} />
        <Route exact path="/insights/hubspot-vs-zoho-vs-og-crm" element={<CrmComparison/>} />
        <Route exact path="/insights/helpdesk-workflow-best-practices" element={<HelpdeskWorkflow/>} />
        <Route exact path="/insights/iso-9001-implementation-guide" element={<Iso9001ImplementationGuide/>} />
        <Route exact path="/insights/mt940-field-reference" element={<Mt940FieldReference/>} />
        <Route exact path="/insights/odata-filter-examples" element={<OdataFilterExamples/>} />
        <Route exact path="/insights/o1js-state-assertions" element={<O1jsStateAssertions/>} />
        <Route exact path="/insights/iso-24874-stellar-sdg-impact" element={<Iso24874StellarSdg/>} />
        <Route exact path="/insights/claude-code-agentic-workflows-mcp" element={<ClaudeCodeMcp/>} />
        <Route exact path="/insights/gpt-6-astra-sol-luna-model-tiers" element={<Gpt6ModelTiers/>} />
        <Route exact path="/insights/devin-autonomous-ai-software-engineer" element={<DevinAutonomous/>} />
        <Route exact path="/insights/deepseek-v4-open-weight-moe-coding-agents" element={<DeepseekV4/>} />
        <Route exact path="/insights/gemini-4-argon-antigravity-agentic-coding" element={<GeminiArgon/>} />
        <Route exact path="/insights/kimi-k3-open-weight-agentic-model" element={<KimiK3/>} />
        <Route exact path="/insights/glm-5-zai-long-horizon-agents" element={<Glm5Zai/>} />
        <Route exact path="/insights/eu-ai-act-compliance-guide" element={<EuAiActCompliance/>} />
        <Route exact path="/insights/nis2-compliance-iso-27001" element={<Nis2Compliance/>} />
        <Route exact path="/insights/cyber-resilience-act-cra-guide" element={<CyberResilienceAct/>} />
        <Route exact path="/insights/en-18286-ai-act-qms" element={<En18286AiQms/>} />
        <Route exact path="/insights/asset-tokenization-explained" element={<AssetTokenization/>} />
        <Route exact path="/insights/ipfs-cid-explained" element={<IpfsCidExplained/>} />
        <Route exact path="/insights/eu-digital-omnibus" element={<DigitalOmnibus/>} />
        <Route exact path="/insights/verification-of-payee-vop" element={<VerificationOfPayee/>} />
        <Route exact path="/insights/agentic-payments-ap2-x402-acp" element={<AgenticPayments/>} />
        <Route exact path="/insights/best-free-crm-small-business" element={<BestFreeCrm/>} />
        <Route exact path="/insights/crm-data-migration-checklist" element={<CrmDataMigration/>} />
        <Route exact path="/glossary" element={<Glossary/>} />
        <Route exact path="/glossary/:slug" element={<GlossaryTerm/>} />
        <Route exact path="/tools" element={<Tools/>} />
        <Route exact path="/tools/html-to-image" element={<HtmlToImage/>} />
        <Route exact path="/tools/screenshot-to-image" element={<ScreenshotToImage/>} />
        <Route exact path="/tools/pdf-tools" element={<PdfTools/>} />
        <Route exact path="/tools/security-tools" element={<SecurityTools/>} />
        <Route exact path="/tools/blockchain-compliance-checker" element={<BlockchainCompliance/>} />
        <Route exact path="/tools/iso-27001-gap-analysis" element={<Iso27001GapAnalysis/>} />
        <Route exact path="/tools/iso-9001-readiness-checker" element={<Iso9001ReadinessChecker/>} />
        <Route exact path="/tools/soc-2-readiness-checker" element={<Soc2ReadinessChecker/>} />
        <Route exact path="/tools/iso-42001-ai-readiness" element={<Iso42001AiReadiness/>} />
        <Route exact path="/tools/iso-8601-validator" element={<Iso8601Validator/>} />
        <Route exact path="/tools/ethereum-toolkit" element={<EthereumToolkit/>} />
        <Route exact path="/tools/aws-arn-parser" element={<AwsArnParser/>} />
        <Route exact path="/tools/iam-policy-validator" element={<IamPolicyValidator/>} />
        <Route exact path="/tools/sap-odata-url-builder" element={<SapODataUrlBuilder/>} />
        <Route exact path="/tools/servicenow-query-builder" element={<ServiceNowQueryBuilder/>} />
        <Route exact path="/tools/hash-generator" element={<HashGenerator/>} />
        <Route exact path="/tools/xml-tools" element={<XmlTools/>} />
        <Route exact path="/tools/xml-formatter" element={<XmlFormatterPage/>} />
        <Route exact path="/tools/xml-validator" element={<XmlValidatorPage/>} />
        <Route exact path="/tools/xml-to-json" element={<XmlToJsonPage/>} />
        <Route exact path="/tools/xml-minifier" element={<XmlMinifierPage/>} />
        <Route exact path="/tools/xpath-tester" element={<XPathTesterPage/>} />
        <Route exact path="/tools/xml-to-csv" element={<XmlToCsvPage/>} />
        <Route exact path="/tools/xsd-validator" element={<XsdValidatorPage/>} />
        <Route exact path="/tools/sepa-xml-validator" element={<SepaXmlValidator/>} />
        <Route exact path="/tools/merge-pdf" element={<PdfMergePage/>} />
        <Route exact path="/tools/split-pdf" element={<PdfSplitPage/>} />
        <Route exact path="/tools/image-to-pdf" element={<ImageToPdfPage/>} />
        <Route exact path="/tools/document-to-pdf" element={<DocToPdfPage/>} />
        <Route exact path="/tools/pdf-to-word" element={<PdfToWordPage/>} />
        <Route exact path="/tools/pdf-metadata" element={<PdfMetadataPage/>} />
        <Route exact path="/tools/wei-converter" element={<WeiConverterPage/>} />
        <Route exact path="/tools/keccak256-hash" element={<Keccak256Page/>} />
        <Route exact path="/tools/function-selector" element={<FunctionSelectorPage/>} />
        <Route exact path="/tools/event-topic-hash" element={<EventTopicPage/>} />
        <Route exact path="/tools/abi-encoder" element={<AbiEncoderPage/>} />
        <Route exact path="/tools/address-checksum" element={<AddressChecksumPage/>} />
        <Route exact path="/tools/iban-validator" element={<IbanValidator/>} />
        <Route exact path="/tools/bic-validator" element={<BicValidator/>} />
        <Route exact path="/tools/lei-validator" element={<LeiValidator/>} />
        <Route exact path="/tools/iso-20022-viewer" element={<Iso20022Viewer/>} />
        <Route exact path="/tools/mt940-to-csv" element={<Mt940ToCsv/>} />
        <Route exact path="/tools/mt940-to-camt053" element={<Mt940ToCamt053/>} />
        <Route exact path="/tools/sepa-xml-generator" element={<SepaXmlGenerator/>} />
        <Route exact path="/tools/hl7-parser" element={<Hl7Parser/>} />
        <Route exact path="/tools/fhir-validator" element={<FhirValidator/>} />
        <Route exact path="/tools/tle-parser" element={<TleParser/>} />
        <Route exact path="/tools/tle-converter" element={<TleConverter/>} />
        <Route exact path="/tools/scorm-validator" element={<ScormValidator/>} />
        <Route exact path="/tools/xapi-statement-builder" element={<XapiStatementBuilder/>} />
        <Route exact path="/tools/gtin-validator" element={<GtinValidator/>} />
        <Route exact path="/tools/gs1-barcode-parser" element={<Gs1BarcodeParser/>} />
        <Route exact path="/tools/geo-checker" element={<GeoChecker/>} />
        <Route exact path="/tools/phone-formatter" element={<PhoneFormatter/>} />
        <Route exact path="/tools/vcard-generator" element={<VcardGenerator/>} />
        <Route exact path="/tools/email-validator" element={<EmailValidator/>} />
        <Route exact path="/tools/csv-deduplicator" element={<CsvDeduplicator/>} />
        <Route exact path="/tools/word-counter" element={<WordCounter/>} />
        <Route exact path="/tools/ipfs-cid-inspector" element={<IpfsCidInspector/>} />
        <Route exact path="/tools/ipfs-gateway-checker" element={<IpfsGatewayChecker/>} />
        <Route exact path="/tools/ipfs-cid-generator" element={<IpfsCidGenerator/>} />
        <Route exact path="/tools/ipfs-url-converter" element={<IpfsUrlConverter/>} />
      </Routes>
        </Suspense>
      <ConsentBanner />
      <ConsentSettingsModal 
        isOpen={showCookieSettings} 
        onClose={() => setShowCookieSettings(false)} 
      />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
          },
          error: {
            duration: 5000,
          },
        }}
      />
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
